<script>
import { mapActions, mapGetters } from 'vuex'
import Layout from '@layouts/main.vue'
import CandidateList from '@/components/candidate/candidate-list.vue'
import CandidateSearchField from '@/components/candidate/candidate-search-field.vue'
import CandidateFilterMenu from '@/components/candidate/candidate-filter-menu.vue'
import CandidateListErrorState from '@/components/candidate/candidate-list-error-state.vue'
import CandidateListEmptyState from '@/components/candidate/candidate-list-empty-state.vue'
import CandidateListSkeleton from '@/components/skeleton/candidate-list-skeleton.vue'
import CandidateZoom from '@/components/candidate/candidate-zoom.vue'
import { resolveSortValue } from '@/shared/constants/candidate/CandidateListFilterMenu'
import { CandidateQueryRequestDTO } from '@/models/candidate/CandidateQueryRequestDTO'
import { CandidateListViews } from '@/shared/constants/candidate/CandidateListViews'

const { All, Preferred } = CandidateListViews

export default {
  name: 'CandidateListContainer',
  components: {
    Layout,
    CandidateList,
    CandidateSearchField,
    CandidateFilterMenu,
    CandidateListErrorState,
    CandidateListEmptyState,
    CandidateListSkeleton,
    CandidateZoom,
  },
  data() {
    return {
      selectedView: All,
      pageNumber: 1,
      loading: false,
      candidates: [],
      totalPages: 0,
      totalCount: 0,
      error: null,
      activeRequestId: 0,
      selectedCandidate: null,
      showCandidateModal: false,
      tabChangeDirection: null,
      tabs: [
        {
          value: All,
          icon: null,
          label: this.$t('candidateList.grouping.all'),
        },
        {
          value: Preferred,
          icon: 'mdi-star-outline',
          label: this.$t('candidateList.grouping.preferred'),
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      selectedClientIds: 'client/getSelectedClients',
      accessibleLocationCount: 'auth/countAllAvailableLocations',
      candidateListFilters: 'candidate/candidateListFilters',
      candidateListSearchTerm: 'candidate/candidateListSearchTerm',
    }),
    hasActiveCandidateListFilters() {
      const {
        sortKey,
        certificationIds,
        clientGroupIds,
        typeIds,
        bookable,
      } = this.candidateListFilters

      const hasCerts = (certificationIds?.length ?? 0) > 0
      const hasGroups = (clientGroupIds?.length ?? 0) > 0
      const hasTypes = (typeIds?.length ?? 0) > 0

      return !!sortKey || bookable !== null || hasCerts || hasGroups || hasTypes
    },
    isSearching() {
      return this.candidateListSearchTerm.length > 0
    },
    candidateListQueryRefetchKey() {
      const {
        selectedClientIds,
        selectedView,
        candidateListSearchTerm,
        accessibleLocationCount,
        candidateListFilters,
      } = this

      return JSON.stringify({
        clients: selectedClientIds,
        selectedTab: selectedView,
        searchTrim: candidateListSearchTerm,
        scope: accessibleLocationCount,
        filters: candidateListFilters,
      })
    },
    showClearFiltersOnEmptyState() {
      return this.isSearching || this.hasActiveCandidateListFilters
    },
    listTransitionName() {
      const transitionMap = {
        left: 'slide-left',
        right: 'slide-right',
      }

      return transitionMap[this.tabChangeDirection] || 'fade-up'
    },
  },
  watch: {
    selectedClientIds(newIds, oldIds) {
      if (JSON.stringify(newIds) !== JSON.stringify(oldIds)) {
        this.resetCandidateListFilters()
      }
    },
    candidateListQueryRefetchKey: {
      handler() {
        this.pageNumber = 1
        this.fetchCandidatesQueryPage()
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      queryCandidates: 'candidate/queryCandidates',
      clearCandidateListSearchAndFilters:
        'candidate/clearCandidateListSearchAndFilters',
      resetCandidateListFilters: 'candidate/resetCandidateListFilters',
    }),
    resetResults() {
      this.candidates = []
      this.totalPages = 0
      this.totalCount = 0
      this.loading = false
      this.tabChangeDirection = null
    },
    showCandidateDialog(candidateId) {
      this.selectedCandidate = candidateId
      this.showCandidateModal = true
    },
    closeCandidateDialog() {
      this.selectedCandidate = null
      this.showCandidateModal = false
    },
    tabRippleBinding(tab) {
      if (this.loading) return false
      if (tab.value === this.selectedView) return false
      return { class: 'primary--text' }
    },
    handleTabChange(view) {
      if (view === this.selectedView) return

      this.tabChangeDirection = view > this.selectedView ? 'left' : 'right'

      this.selectedView = view
    },
    goToPage(page) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      this.pageNumber = page
      this.fetchCandidatesQueryPage()
    },
    async fetchCandidatesQueryPage() {
      const requestId = ++this.activeRequestId

      if (this.accessibleLocationCount === 0) {
        this.resetResults()
        this.error = { noScope: true }
        return
      }

      const {
        sortKey,
        sortDir,
        bookable,
        certificationIds,
        clientGroupIds,
        typeIds,
      } = this.candidateListFilters

      const payload = new CandidateQueryRequestDTO({
        clientIds: this.selectedClientIds,
        grouping: this.isSearching ? CandidateListViews.All : this.selectedView,
        searchTerm: this.isSearching ? this.candidateListSearchTerm : '',
        sort: resolveSortValue(sortKey, sortDir),
        bookable,
        certificationIds,
        clientGroupIds,
        typeIds,
        pageNumber: this.pageNumber,
      })

      this.loading = true

      const { data, error, statusCode } = await this.queryCandidates(payload)

      if (requestId !== this.activeRequestId) return

      if (error) {
        this.resetResults()
        this.error = { error, statusCode }
        return
      }

      this.error = null
      this.candidates = data?.candidates ?? []
      this.totalPages = data?.totalPages ?? 0
      this.totalCount = data?.totalCount ?? 0
      this.tabChangeDirection = null

      if (requestId === this.activeRequestId) {
        this.loading = false
      }
    },
  },
}
</script>

<template>
  <Layout no-gutters>
    <div class="white px-6 pt-6">
      <BasePageHeader :title="$t('candidateList.pageTitle')" class="mb-0">
        <template v-slot:actions-prepend>
          <transition name="fast-fade">
            <div v-if="!error" class="candidate-filter-bar d-flex align-center">
              <CandidateSearchField :disabled="loading" />
              <CandidateFilterMenu :disabled="loading" />
            </div>
          </transition>
        </template>
      </BasePageHeader>

      <v-tabs
        :value="selectedView"
        class="candidate-list__tabs mt-6"
        background-color="transparent"
        :color="!!error || loading ? 'grey lighten-1' : 'primary'"
        @change="handleTabChange"
      >
        <v-tab
          v-for="tab in tabs"
          :key="`view-${tab.value}`"
          :value="tab.value"
          :disabled="!!error || loading"
          :ripple="tabRippleBinding(tab)"
        >
          <v-icon v-if="tab.icon" left small>{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </v-tab>
      </v-tabs>
    </div>

    <section class="px-3 pt-3">
      <transition :name="listTransitionName" mode="out-in">
        <CandidateListSkeleton v-if="loading" key="loading" />

        <CandidateListErrorState
          v-else-if="error"
          key="error"
          :error="error"
          @retry="fetchCandidatesQueryPage"
        />

        <CandidateListEmptyState
          v-else-if="!candidates.length"
          key="empty"
          :show-clear-cta="showClearFiltersOnEmptyState"
          @clear="clearCandidateListSearchAndFilters"
        />

        <CandidateList
          v-else
          key="list"
          :items="candidates"
          :page-number="pageNumber"
          :total-pages="totalPages"
          @go-to-page="goToPage"
          @view-candidate="showCandidateDialog"
        />
      </transition>
    </section>

    <v-dialog
      v-if="showCandidateModal"
      v-model="showCandidateModal"
      width="720px"
      content-class="rounded-xl"
    >
      <CandidateZoom
        :id="selectedCandidate"
        @closeModal="closeCandidateDialog"
      />
    </v-dialog>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/_vue-transitions.scss';

.candidate-list__tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  .v-tab:not(.v-tab--active):hover {
    color: $color-blue-500;
    background: $color-info-250;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;

    .v-icon {
      color: $color-blue-500;
    }
  }

  .v-tab--active {
    cursor: default;

    &:hover::before {
      opacity: 0 !important;
    }
  }
}
</style>
